import React, { useState, useEffect, useCallback, useMemo } from "react";
import GenericInput from "../../../genericComponents/input/GenericInputComponent";
import GenericCustomButton from "../../../genericComponents/GenericCustomButton";
import Icons from "../../../assets/icons";
import useCustomToast from "../../../hooks/useCustomToast";
import { ClipboardCheck, X } from "lucide-react";
import ParentModal from "../../../genericComponents/ParentModal";
import GenericConfirmationModal from "../../../genericComponents/GenericConfiramationModal";
import { useDispatch, useSelector } from "react-redux";
import { dispatchHandler } from "../../../helper/apiHelperFunctions";
import { getPermissionKey } from "../../../helper/permissions";
import { CONFIG } from "../../../config";
import { v4 as uuidv4 } from "uuid";
import { parseJwt } from "../../../helper/genericFunction";

let careInstructionIcons = [
    { key: "Washing", icon: <Icons.Washing /> },
    { key: "Bleach", icon: <Icons.Bleach /> },
    { key: "Tumble Dry", icon: <Icons.TumbleDry /> },
    { key: "Iron", icon: <Icons.Iron /> },
    { key: "Dry Clean", icon: <Icons.DryClean /> }
];

const formFields = {
    vendorCode: { label: "Vendor Code", type: "text", required: true },
    vendorName: { label: "Vendor Name", type: "text", required: true },
    gstin: { label: "GSTIN", type: "text", required: true },
    vendorAddress: { label: "Vendor Address", type: "text", required: true },
    contactPerson: { label: "Contact Person", type: "text", required: true },
    contactNumber: { label: "Mobile", type: "text", required: true },
    alternateContactNumber: { label: "Telephone", type: "text", required: false },
    email: { label: "Email", type: "text", required: false },
    invoiceTo: { label: "Invoice To", type: "text", required: true },
    billingAddress: { label: "Billing Address", type: "text", required: true },
    buyerName: { label: "Buyer Name", type: "text", required: true },
    category: { label: "Category", type: "text", required: false },
    department: { label: "Department", type: "text", required: true },
    division: { label: "Division", type: "text", required: true },
    sampleDescription: { label: "Sample Description", type: "text", required: false },
    designNo: { label: "Design No", type: "text", required: true },
    orderNumber: { label: "PO Number", type: "text", required: true },
    ageGroup: { label: "Age Group", type: "text", required: false },
    color: { label: "Color", type: "text", required: true },
    endUse: { label: "End Use", type: "text", required: false },
    labName: { label: "Lab Master", type: "dropdown", required: true },
    yarnCount: { label: "Yarn Count", type: "text", required: true },
    fabricComposition: { label: "Fabric Composition", type: "text", required: true },
    fabricConstruction: { label: "Construction", type: "text", required: true },
    fabricWeight: { label: "Fabric Weight", type: "text", required: true },
    careInstructions: { label: "Care Instructions", type: "text", required: true },
    otherCareInstruction: { label: "Other Care Instruction", type: "text", required: false },
    packageTestFPT: { label: "Package Test-FPT", type: "text", required: false },
    packageTestGPT: { label: "Package Test-GPT", type: "text", required: false },
    additionalTestRequired: { label: "Additional Test Required", type: "text", required: false },
    typeOfPrint: { label: "Type of Print", type: "text", required: false },
    labCode: { label: "Lab Code", type: "text", required: false },
}


const TrfModal = (props) => {
    const { trfData, orderNumber, viewTrfMode, closeModal, testTrfData, justFormVisible = false, isDirectTRF = false, onFetchDirectTRF, designColorData = [] } = props;

    const [designNo, setDesignNo] = useState(null);
    const [trfFormData, setTrfFormData] = useState({});
    const [selectAllTest, setSelectAllTest] = useState({
        packageTestFPT: false,
        packageTestGPT: false,
        additionalTestRequired: false
    })
    const [selectAllCareInstruction, setSelectAllCareInstructions]= useState(false);
    const dispatch = useDispatch();
    const customToast = useCustomToast()
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [pendingDesignNo, setPendingDesignNo] = useState(null);
    const submitTrfState = useSelector((state) => state.digivendStore?.submitTrfData);
    const [errorData, setErrorData] = useState(null);

    // Direct TRF mode state (when opening without PO selection)
    const [isDirectDataLoaded, setIsDirectDataLoaded] = useState(false);
    const [directDesignNo, setDirectDesignNo] = useState("");
    const [directColor, setDirectColor] = useState("");
    const getTRFState = useSelector((state) => state.digivendStore?.getTRFData);

    // Track when direct TRF data is successfully fetched
    useEffect(() => {
        if (isDirectTRF && getTRFState?.isSuccess && !isDirectDataLoaded) {
            setIsDirectDataLoaded(true);
        }
    }, [isDirectTRF, getTRFState?.isSuccess]);

    // Auto-select first design number from designColorData when it arrives
    useEffect(() => {
        if (isDirectTRF && designColorData?.length > 0 && !directDesignNo) {
            const firstDesign = designColorData[0]?.design_no || "";
            setDirectDesignNo(firstDesign);
            // Auto-select color for first design
            const matchingColor = designColorData.find(item => item.design_no === firstDesign)?.color || "";
            setDirectColor(matchingColor);
        }
    }, [isDirectTRF, designColorData]);

    // Auto-select color when directDesignNo changes (from designColorData)
    useEffect(() => {
        if (isDirectTRF && designColorData?.length > 0 && directDesignNo) {
            const matchingItem = designColorData.find(item => item.design_no === directDesignNo);
            if (matchingItem) {
                setDirectColor(matchingItem.color || "");
            }
        }
    }, [directDesignNo, designColorData]);

    const handleFetchDirectTRF = () => {
        if (!directDesignNo?.trim()) {
            customToast({
                title: "Design Number is required",
                description: "Please enter a Design Number to fetch TRF data.",
                status: "error",
                duration: 3000,
                isClosable: true
            });
            return;
        }
        if (!directColor?.trim()) {
            customToast({
                title: "Color is required",
                description: "Please enter a Color to fetch TRF data.",
                status: "error",
                duration: 3000,
                isClosable: true
            });
            return;
        }
        if (onFetchDirectTRF) {
            onFetchDirectTRF({ designNo: directDesignNo.trim(), color: directColor.trim() });
        }
    };

    useEffect(() => {
        if(!showConfirmationModal) {
            let tokenData = parseJwt(sessionStorage?.getItem("token"));
            sessionStorage.setItem("X-REQUEST-ID", uuidv4() + (tokenData.jti || ""));
        }
    }, [showConfirmationModal])

    useEffect(() => {
        if (!trfData || !designNo || !trfData[designNo]) return;
        setTrfFormData({ ...trfData[designNo], isSameBillingAddress: false });
        setSelectAllTest({
            packageTestFPT: Array.isArray(trfData[designNo]?.packageTestFPT) &&
                trfData[designNo].packageTestFPT.length > 0 &&
                (testTrfData?.['TRF_FPT_TEST'] || [])?.every(item => trfData[designNo].packageTestFPT.includes(item)),
            packageTestGPT: Array.isArray(trfData[designNo]?.packageTestGPT) &&
                trfData[designNo].packageTestGPT.length > 0 &&
                (testTrfData?.['TRF_GPT_TEST'] || [])?.every(item => trfData[designNo].packageTestGPT.includes(item)),
            additionalTestRequired: Array.isArray(trfData[designNo]?.additionalTestRequired) &&
                trfData[designNo].additionalTestRequired.length > 0 &&
                (testTrfData?.['TRF_ADDITIONAL_TEST'] || [])?.every(item => trfData[designNo].additionalTestRequired.includes(item))
        })
        setSelectAllCareInstructions(Array.isArray(trfData[designNo]?.careInstructions) && trfData[designNo]?.careInstructions?.length > 0 &&
            testTrfData?.['TRF_CARE_INSTRUCTIONS']?.every(item => trfData[designNo].careInstructions.includes(item)));

    }, [designNo, testTrfData]);


    useEffect(() => {
        if (!trfData || Object.keys(trfData).length === 0) return;

        const firstKey = Object.keys(trfData)[0];
        setDesignNo(firstKey);
        setTrfFormData({ ...trfData[firstKey], isSameBillingAddress: false });

    }, [trfData]);

    const handleInputChange = useCallback((key, value, nextVal = false) => {
        if (viewTrfMode) return;
        if (key === "careInstructions") {
            if(Array.isArray(value)){
                setTrfFormData((prev) => ({
                    ...prev,
                    [key]: nextVal ? value : []
                }))
                return;
            }
            setTrfFormData(prev => {
                const newValue = prev[key]?.includes(value)
                    ? prev[key].filter(item => item !== value)
                    : [...prev[key], value];
                const allSelected= testTrfData?.['TRF_CARE_INSTRUCTIONS']?.every(item => newValue.includes(item));
                setSelectAllCareInstructions(allSelected);
                return {
                    ...prev,
                    [key]: newValue
                }
            })
            return;
        }
        else if (key === "additionalTestRequired" || key === "packageTestGPT" || key === "packageTestFPT") {
            if (Array.isArray(value)) {
                setTrfFormData((prev) => ({
                    ...prev,
                    [key]: nextVal ? value : []
                }))
                return;
            }

            setTrfFormData(prev => {
                const newValue = prev[key]?.includes(value)
                    ? prev[key].filter(item => item !== value)
                    : [...(prev[key] || []), value];

                const testType = key === 'packageTestFPT' ? 'packageTestFPT' :
                    key === 'packageTestGPT' ? 'packageTestGPT' : 'additionalTestRequired';
                const testArray = testType === 'packageTestFPT' ? (testTrfData?.['TRF_FPT_TEST'] || []) :
                    testType === 'packageTestGPT' ? (testTrfData?.['TRF_GPT_TEST'] || []) : (testTrfData?.['TRF_ADDITIONAL_TEST'] || []);

                const allSelected = testArray.every(item => newValue.includes(item));

                setSelectAllTest(prev => ({
                    ...prev,
                    [testType]: allSelected
                }));

                return {
                    ...prev,
                    [key]: newValue
                };
            });
            return;
        }
        else if (key === "labName") {
            setTrfFormData(prev => {
                return { ...prev, labCode: value?.labCode, labName: value?.concat };
            });
            return;
        }
        setTrfFormData(prev => {
            if (prev[key] !== undefined) {
                return { ...prev, [key]: value };
            }
            return { ...prev, [key]: value };
        });
    }, [trfFormData]);

    const handleClearData = () => {
        if (viewTrfMode) return;
        setTrfFormData(prev => {
            const source = trfData?.[designNo] || {};

            const updated = {};
            Object.keys(formFields).forEach(key => {

                if (source[key] !== undefined && source[key] !== null && source[key] !== "") {
                    updated[key] = source[key];
                } else {
                    updated[key] = "";
                }

            })
            return {
                ...prev,
                isSameBillingAddress: false,
                ...updated
            }
        });
        setSelectAllTest({
            packageTestFPT: false,
            packageTestGPT: false,
            additionalTestRequired: false
        });
        setSelectAllCareInstructions(false);
    };

    useEffect(() => {
        if (submitTrfState?.isSuccess) {
            const responseData = submitTrfState?.data;
            customToast({
                title: "TRF Data Submitted Successfully",
                description: `TRF Code: ${responseData?.trfCode || ""}`,
                status: "success",
                duration: 3000,
                isClosable: true
            });
            closeModal();
            sessionStorage.removeItem("X-REQUEST-ID");
        }
        else if (submitTrfState?.isError) {
            customToast({
                title: "Error in Submitting TRF Data",
                description: submitTrfState?.errorMessage,
                status: "error",
                duration: 3000,
                isClosable: true
            });
        }
    }, [submitTrfState]);

    useEffect(() => {
        return () => {
            dispatchHandler(dispatch, "submitTrfClearRequest");
        }
    }, []);

    const handleSubmit = () => {
        if (viewTrfMode) return;
        const missingFields = Object.keys(formFields).filter((key) => {
            const { required } = formFields[key];
            const value = trfFormData[key];

            if (required && (!value || value === "" || !Array.isArray(value) && value.trim() === "")) {
                return true;
            }

            return false;
        });
        if (trfFormData?.careInstructions?.length === 0 && !trfFormData?.otherCareInstruction) {
            missingFields.push("careInstructions");
        }
        if (missingFields.length > 0) {
            customToast({
                title: "Missing Fields",
                description: `Please fill in the following fields: ${missingFields.join(", ")}`,
                status: "error",
                duration: 3000,
                isClosable: true
            });
            return;
        }
        const excludeKeys = ["typeOfPrint", "category", "endUse","labName"];  //currently we are not sending these to payload
        const includeKeys = ["labCode"];  // we include this key to payload as we get it through labName
        const payload = Object.keys(formFields).reduce((acc, key) => {
            if (excludeKeys.includes(key)) return acc;
            const value = trfFormData[key];
            if (Array.isArray(value) && value.length === 0) {
                acc[key] = null;
            } else {
                acc[key] = value ?? null;
            }
            return acc;
        }, {});
        includeKeys.forEach(key => {
            payload[key] = trfFormData[key];
        });
        dispatchHandler(dispatch, "submitTrfRequest", payload, getPermissionKey("vendor", "purchase", "trf.submit"));
    };

    const CustomCheckbox = ({ checked, onChange, label }) => (
        <label className={`generic-checkbox`}>
            <input
                type="checkbox"
                checked={checked}
                disabled={viewTrfMode}
                onChange={(e) => {
                    onChange(e.target.checked);
                }}
            />

            <span className={`checkmark1 ${viewTrfMode ? "opacity-25" : ""}`}></span>

            <span className="text-[#67768e] text-xs font-medium">
                {label}
            </span>
        </label>
    );

    // In direct TRF mode, hide the full form until data is fetched
    const isDirectTRFPending = isDirectTRF && !isDirectDataLoaded;

    return (

        <div className="flex flex-col h-full w-full bg-white rounded-lg overflow-hidden">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-white sticky top-0 z-20">
                <div className="flex items-center gap-2">
                    {viewTrfMode ? <ClipboardCheck size={20} /> : <Icons.CopyPenIcon size={20} className="text-gray-700" />}

                    <h2 className="text-lg font-bold text-gray-900 m-0">Test Request Form(TRF)</h2>
                    {
                        viewTrfMode && (
                            <div className="flex items-center justify-center px-[15px] py-[5px] rounded-full bg-[#F0F3F6]">
                                <span className="text-xs font-medium text-[#67768E]">{trfFormData?.trfCode}</span>
                            </div>
                        )
                    }
                </div>
                <div className="flex items-center gap-4 justify-center">
                    {!isDirectTRFPending && <GenericInput
                        inputKey="labName"
                        errorData={errorData}
                        placeholder="Select Lab"
                        setErrorData={setErrorData}
                        iconRight={<Icons.DownArrowNew />}
                        type="dropdown"
                        mandatory={formFields?.labName?.required}
                        value={trfFormData?.labName || ""}
                        inputDropdownExtraClass={"!text-clip !whitespace-normal !overflow-visible"}
                        genInputExtraClass= "!mb-0 !whitespace-normal !overflow-visible !text-clip"
                        onChange={(e) => {
                            handleInputChange("labName", { concat: e.concat, labCode: e.labCode })
                        }}
                        isDisabled={viewTrfMode || trfData?.[designNo]?.labName}
                        inputClassName="bg-gray-50 border-gray-200 !whitespace-normal !text-clip !overflow-visible"
                        hideInputTooltip={true}
                        inputComponents={!viewTrfMode && {
                            inputExtra: () => {
                                return (
                                    <span className="generic-tooltip  !-top-[7px] !-left-[55%]  after:!border-transparent after:!border-l-black after:!top-[57%] after:!left-[103%]">Select the lab to which Test <br />Requirements will be sent</span>
                                )
                            }
                        }}
                        inputProps={() => ({
                            headerKeys: {
                                concat: { value: "concat" }
                            },
                            itemKeys: {
                                concat: ["concat"]
                            },
                            search: {
                                placeholder: `Search Lab`,
                            },
                            api: {
                                reduxState: "generalStore",
                                apiName: "getSearchFilters",
                                apiPayload: (params) => {
                                    const payload = {
                                        apiUrl: "/plan/settings/search/arsGenericFilters",
                                        baseUrl: CONFIG.BASE_URL,
                                        key: "concat(lab_name,'\'' '\'',location)",
                                        code: "lab_code",
                                        pageNo: params?.currentPage || 1,
                                        pageSize: 10,
                                        searchBy: "CONTAINS",
                                        search: params?.currentSearch || "",
                                        filter: {},
                                        entity: "lab_master"
                                    }
                                    return payload;
                                },
                                getMaxPage: (states, functions, response) => Number(response?.maxPage || ""),
                            },
                            getListData: (response) => {
                                return response?.resource?.searchResultNew?.map(item => ({
                                    concat: item?.concat,
                                    labCode: item?.lab_code
                                }))
                            }
                        }
                        )}
                    />}

                    {!justFormVisible && <div className="flex items-center gap-6">
                        <button onClick={closeModal} className="flex items-center gap-1 text-gray-500 hover:text-gray-700 text-sm font-medium">
                            Esc <span className="text-gray-300">|</span> <X size={20} />
                        </button>
                    </div>}
                </div>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-5 bg-white">

                {isDirectTRFPending && (
                    <div className="mb-8 border border-gray-200 py-6 px-7 rounded-lg bg-gray-50">
                        <h3 className="text-sm font-bold text-gray-900 mb-4">
                            Select Design Details
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
                            <GenericInput
                                label={formFields?.designNo?.label}
                                inputKey="directDesignNo"
                                type="dropdown"
                                mandatory={true}
                                hideInputTooltip={true}
                                iconRight={<Icons.DownArrowNew />}
                                value={directDesignNo}
                                onChange={(data) => {
                                    setDirectDesignNo(data?.design_no || "");
                                    if(data?.color) {
                                        setDirectColor(data?.color)
                                    }
                                }}
                                inputClassName="bg-white border-gray-300"
                                genInputExtraClass="!m-0"
                                placeholder="Select / Search Design Number"
                                dependentFieldsObject={[directDesignNo, designColorData]}
                                inputProps={() => ({
                                    headerKeys: {
                                        design_no: { value: "Design No" }
                                    },
                                    itemKeys: {
                                        design_no: ["design_no"]
                                    },
                                    search: {
                                        placeholder: "Search Design No",
                                    },
                                    localList: designColorData?.length > 0
                                        ? [...new Map(designColorData.map(item => [item.design_no, { design_no: item.design_no }])).values()]
                                        : undefined,
                                    ...(designColorData?.length > 0 ? {
                                        getListData: () => designColorData?.map((item) => ({design_no: item.design_no, color: item?.color}))
                                    } : {
                                        api: {
                                            reduxState: "generalStore",
                                            apiName: "getSearchFilters",
                                            apiPayload: (params) => {
                                                return {
                                                    key: "design_no",
                                                    code: "",
                                                    entity: "item",
                                                    pageNo: params?.currentPage || 1,
                                                    pageSize: 10,
                                                    searchBy: "CONTAINS",
                                                    search: params?.currentSearch || "",
                                                    other_data: directColor ? { color: [directColor] } : {},
                                                    columnName: {}
                                                };
                                            },
                                            getMaxPage: (states, functions, response) => Number(response?.resource?.maxPage || response?.maxPage || ""),
                                        },
                                        getListData: (response) => {
                                            return response?.resource?.searchResultNew?.map(item => ({
                                                design_no: item?.design_no || item,
                                            })) || []
                                        }
                                    })
                                })}
                            />
                            <GenericInput
                                label={formFields?.color?.label}
                                inputKey="directColor"
                                type="dropdown"
                                mandatory={true}
                                hideInputTooltip={true}
                                iconRight={<Icons.DownArrowNew />}
                                value={directColor}
                                onChange={(data) => {
                                    setDirectColor(data?.color || data?.value || "");
                                }}
                                dependentFieldsObject={[directColor, directDesignNo, designColorData]}
                                inputClassName="bg-white border-gray-300"
                                genInputExtraClass="!m-0"
                                placeholder="Select / Search Color"
                                isDisabled={designColorData?.length > 0}
                                inputProps={() => ({
                                    headerKeys: {
                                        color: { value: "Color" }
                                    },
                                    itemKeys: {
                                        color: ["color"]
                                    },
                                    search: {
                                        placeholder: "Search Color",
                                    },
                                    localList: designColorData?.length > 0
                                        ? designColorData
                                            .filter(item => !directDesignNo || item.design_no === directDesignNo)
                                            .map(item => ({ color: item.color }))
                                        : undefined,
                                    ...(designColorData?.length > 0 ? {} : {
                                        api: {
                                            reduxState: "generalStore",
                                            apiName: "getSearchFilters",
                                            apiPayload: (params) => {
                                                return {
                                                    key: "color",
                                                    code: "",
                                                    entity: "item",
                                                    pageNo: params?.currentPage || 1,
                                                    pageSize: 10,
                                                    searchBy: "CONTAINS",
                                                    search: params?.currentSearch || "",
                                                    other_data: directDesignNo ? { design_no: [directDesignNo] } : {},
                                                    columnName: {}
                                                };
                                            },
                                            getMaxPage: (states, functions, response) => Number(response?.resource?.maxPage || response?.maxPage || ""),
                                        },
                                        getListData: (response) => {
                                            return response?.resource?.searchResultNew?.map(item => ({
                                                color: item?.color
                                            })) || []
                                        }
                                    })
                                })}
                            />
                            <GenericCustomButton
                                label={getTRFState?.isLoading ? "Fetching..." : "Fetch TRF Data"}
                                type="primary"
                                onClick={handleFetchDirectTRF}
                                disabled={getTRFState?.isLoading || !directDesignNo || !directColor}
                            />
                        </div>
                    </div>
                )}

                {!isDirectTRFPending && (<>
                <div className="mb-8 border border-gray-200 py-6 px-7 rounded-lg bg-gray-50">
                    <h3 className="text-base font-bold text-gray-900 mb-4">
                        {trfFormData?.vendorName || ""}
                    </h3>

                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">

                        <div className="flex flex-col gap-1 lg:col-span-1">
                            <label className="flex items-center gap-1 text-xs font-semibold text-gray-500 mb-1">
                                <span><Icons.GstIcon size={12} /></span>
                                <span>GST Number</span>
                            </label>
                            <div className="text-sm font-medium text-gray-900">
                                {trfFormData?.gstin || ""}
                            </div>
                        </div>

                        <div className="flex flex-col gap-1 lg:col-span-3">
                            <label className="flex items-center gap-1 text-xs font-semibold text-gray-500 mb-1">
                                <span><Icons.LocationIcon size={12} /></span>
                                <span>Vendor Address</span>
                            </label>
                            <div className="text-sm font-medium text-gray-900 break-words">
                                {trfFormData?.vendorAddress || ""}
                            </div>
                        </div>

                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">

                        <GenericInput
                            label={formFields?.contactPerson?.label}
                            inputKey="contactPerson"
                            errorData={errorData}
                            setErrorData={setErrorData}
                            type="text"
                            hideInputTooltip={true}
                            isDisabled={viewTrfMode || trfData?.[designNo]?.contactPerson}
                            mandatory={formFields?.contactPerson?.required}
                            value={trfFormData?.contactPerson || ""}
                            onChange={(e) => handleInputChange("contactPerson", e.target?.value)}
                            inputClassName="bg-gray-50 border-gray-200  !whitespace-normal !text-clip !overflow-visible"
                            genInputExtraClass= "!whitespace-normal !overflow-visible !text-clip"
                        />

                        <GenericInput
                            label={formFields?.contactNumber?.label}
                            inputKey="contactNumber"
                            errorData={errorData}
                            setErrorData={setErrorData}
                            type="mobileNo"
                            hideInputTooltip={true}
                            isDisabled={viewTrfMode || trfData?.[designNo]?.contactNumber}
                            mandatory={formFields?.contactNumber?.required}
                            value={trfFormData?.contactNumber || ""}
                            onChange={(e) => handleInputChange("contactNumber", e.target?.value)}
                            inputClassName="bg-gray-50 border-gray-200  !whitespace-normal !text-clip !overflow-visible"
                            genInputExtraClass= "!whitespace-normal !overflow-visible !text-clip"
                        />

                        <GenericInput
                            label={formFields?.alternateContactNumber?.label}
                            inputKey="alternateContactNumber"
                            errorData={errorData}
                            setErrorData={setErrorData}
                            hideInputTooltip={true}
                            type="text"
                            isDisabled={viewTrfMode || trfData?.[designNo]?.alternateContactNumber}
                            mandatory={formFields?.alternateContactNumber?.required}
                            value={trfFormData?.alternateContactNumber || ""}
                            onChange={(e) => handleInputChange("alternateContactNumber", e.target?.value)}
                            inputClassName="bg-gray-50 border-gray-200  !whitespace-normal !text-clip !overflow-visible"
                            genInputExtraClass= "!whitespace-normal !overflow-visible !text-clip"
                        />

                        <GenericInput
                            label={formFields?.email?.label}
                            inputKey="email"
                            errorData={errorData}
                            setErrorData={setErrorData}
                            type="email"
                            hideInputTooltip={true}
                            isDisabled={viewTrfMode || trfData?.[designNo]?.email}
                            mandatory={formFields?.email?.required}
                            value={trfFormData?.email || ""}
                            onChange={(e) => handleInputChange("email", e.target?.value)}
                            inputClassName="bg-gray-50 border-gray-200  !whitespace-normal !text-clip !overflow-visible"
                            genInputExtraClass= "!whitespace-normal !overflow-visible !text-clip"
                        />

                    </div>
                </div>


                <div className="mb-8">
                    <h3 className="text-sm font-bold text-gray-900">
                        Billing Details
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 items-end">
                        <div>
                            <GenericInput
                                label={() => {
                                    return (
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-[#67768e] font-bold text-xs">
                                                {formFields?.invoiceTo?.label} {formFields?.invoiceTo?.required && <span className='mandatory'>*</span>}
                                            </span>

                                            {viewTrfMode ? null : <label className="generic-checkbox">
                                                <input
                                                    type="checkbox"
                                                    disabled={viewTrfMode}
                                                    checked={trfFormData?.isSameBillingAddress || false}
                                                    onChange={(e) => handleInputChange("isSameBillingAddress", (e.target?.checked || false))}
                                                />

                                                <span className="checkmark1"></span>

                                                <span className="text-[#67768e] text-xs font-medium">
                                                    Same as above
                                                </span>
                                            </label>}
                                        </div>
                                    )
                                }}
                                hideLabel
                                inputKey="invoiceTo"
                                hideInputTooltip={true}
                                errorData={errorData}
                                setErrorData={setErrorData}
                                type="text"
                                mandatory={formFields?.invoiceTo?.required}
                                isDisabled={viewTrfMode || trfData?.[designNo]?.invoiceTo}
                                value={trfFormData?.invoiceTo || ""}
                                onChange={(e) => handleInputChange("invoiceTo", e.target?.value)}
                                inputClassName="bg-gray-50 border-gray-200  !whitespace-normal !text-clip !overflow-visible"
                                genInputExtraClass= "!whitespace-normal !overflow-visible !text-clip"
                            />

                        </div>

                        <div>
                            <GenericInput
                                label={formFields?.billingAddress?.label}
                                inputKey="billingAddress"
                                errorData={errorData}
                                setErrorData={setErrorData}
                                type="text"
                                mandatory={trfFormData?.isSameBillingAddress ? false : formFields?.billingAddress?.required}
                                value={trfFormData?.isSameBillingAddress ? trfFormData?.vendorAddress : (trfFormData?.billingAddress || "")}
                                onChange={(e) => {
                                    handleInputChange("billingAddress", e.target?.value)
                                }}
                                isDisabled={viewTrfMode || trfFormData?.isSameBillingAddress}
                                inputClassName="bg-gray-50 border-gray-200 !whitespace-normal !text-clip !overflow-visible"
                                hideInputTooltip={true}
                                genInputExtraClass= "!whitespace-normal !overflow-visible !text-clip"
                            />
                        </div>
                    </div>
                </div>

                <div className="mb-8">
                    <h3 className="text-sm font-bold text-gray-900 mb-4">
                        PO Details
                    </h3>

                    <div className="grid grid-cols-6 gap-4 mb-4">
                        <div className="col-span-2">
                            <GenericInput
                                label={formFields?.buyerName?.label}
                                inputKey="buyerName"
                                errorData={errorData}
                                setErrorData={setErrorData}
                                hideInputTooltip={true}
                                type="text"
                                mandatory={formFields?.buyerName?.required}
                                value={trfFormData?.buyerName || ""}
                                onChange={(e) => handleInputChange("buyerName", e.target?.value)}
                                isDisabled={viewTrfMode || trfData?.[designNo]?.buyerName}
                                inputClassName="bg-gray-50 border-gray-200  !whitespace-normal !text-clip !overflow-visible"
                                genInputExtraClass= "!whitespace-normal !overflow-visible !text-clip"
                            />
                        </div>
                        <div className="col-span-1">
                            <GenericInput
                                label={formFields?.orderNumber?.label}
                                inputKey="orderNumber"
                                hideInputTooltip={true}
                                errorData={errorData}
                                setErrorData={setErrorData}
                                type="text"
                                isDisabled={viewTrfMode || trfData?.[designNo]?.orderNumber}
                                mandatory={formFields?.orderNumber?.required}
                                value={trfFormData?.orderNumber || trfFormData?.poNumber || orderNumber || ""}
                                onChange={(e) => handleInputChange("orderNumber", e.target?.value)}
                                inputClassName="bg-gray-50 border-gray-200  !whitespace-normal !text-clip !overflow-visible"
                                genInputExtraClass= "!whitespace-normal !overflow-visible !text-clip"
                            />
                        </div>
                        <div className="col-span-1">
                            {designNo ? <GenericInput
                                label={formFields?.designNo?.label}
                                inputKey="designNo"
                                errorData={errorData}
                                setErrorData={setErrorData}
                                hideInputTooltip={true}
                                type="dropdown"
                                gibInnerClassName={"!text-clip !whitespace-normal !overflow-visible"}
                                mandatory={formFields?.designNo?.required}
                                iconRight={<Icons.DownArrowNew />}
                                value={designNo}
                                isDisabled={viewTrfMode}
                                onChange={(data) => {
                                    const value = data?.value;
                                    if (value !== designNo) {
                                        setShowConfirmationModal(true);
                                        setPendingDesignNo(value)
                                    }
                                }}
                                inputProps={() => ({
                                    headerKeys: {
                                        "value": {}
                                    },
                                    itemKeys: {
                                        "value": ["value"]
                                    },
                                    getListData: () => Object.keys(trfData).map(key => ({ value: key }))
                                })}
                                inputClassName="bg-gray-50 border-gray-200  !whitespace-normal !text-clip !overflow-visible"
                                genInputExtraClass= "!whitespace-normal !overflow-visible !text-clip"
                            /> :
                                <GenericInput
                                    label={formFields?.designNo?.label}
                                    inputKey="designNo"
                                    errorData={errorData}
                                    setErrorData={setErrorData}
                                    mandatory={formFields?.designNo?.required}
                                    type="text"
                                    hideInputTooltip={true}
                                    value={trfFormData?.designNo}
                                    onChange={(e) => handleInputChange("designNo", e.target?.value)}
                                    isDisabled={viewTrfMode}
                                    inputClassName="bg-gray-50 border-gray-200  !whitespace-normal !text-clip !overflow-visible"
                                    genInputExtraClass= "!whitespace-normal !overflow-visible !text-clip"
                                />
                            }
                        </div>
                    </div>

                    <div className="grid grid-cols-6 gap-4 mb-4">
                        <div>
                            <GenericInput
                                label={formFields?.ageGroup?.label}
                                inputKey="ageGroup"
                                errorData={errorData}
                                setErrorData={setErrorData}
                                type="text"
                                hideInputTooltip={true}
                                mandatory={formFields?.ageGroup?.required}
                                isDisabled={viewTrfMode || trfData?.[designNo]?.ageGroup}
                                value={trfFormData?.ageGroup}
                                onChange={(e) => handleInputChange("ageGroup", e.target?.value)}
                                inputClassName="bg-gray-50 border-gray-200  !whitespace-normal !text-clip !overflow-visible"
                                genInputExtraClass= "!whitespace-normal !overflow-visible !text-clip"
                            />
                        </div>

                        <div>
                            <GenericInput
                                label={formFields?.department?.label}
                                inputKey="department"
                                errorData={errorData}
                                hideInputTooltip={true}
                                setErrorData={setErrorData}
                                mandatory={formFields?.department?.required}
                                isDisabled={viewTrfMode || trfData?.[designNo]?.department}
                                value={trfFormData?.department}
                                onChange={(e) => handleInputChange("department", e.target?.value)}
                                inputClassName="bg-gray-50 border-gray-200  !whitespace-normal !text-clip !overflow-visible"
                                genInputExtraClass= "!whitespace-normal !overflow-visible !text-clip"
                            />
                        </div>
                        <div>
                            <GenericInput
                                label={formFields?.category?.label}
                                inputKey="category"
                                errorData={errorData}
                                setErrorData={setErrorData}
                                hideInputTooltip={true}
                                mandatory={formFields?.category?.required}
                                isDisabled={viewTrfMode || trfData?.[designNo]?.category}
                                value={trfFormData?.category}
                                onChange={(e) => handleInputChange("category", e.target?.value)}
                                inputClassName="bg-gray-50 border-gray-200  !whitespace-normal !text-clip !overflow-visible"
                                genInputExtraClass= "!whitespace-normal !overflow-visible !text-clip"
                            />
                        </div>
                        <div className="col-span-2">
                            <GenericInput
                                label={formFields?.sampleDescription?.label}
                                inputKey="sampleDescription"
                                errorData={errorData}
                                setErrorData={setErrorData}
                                hideInputTooltip={true}
                                mandatory={formFields?.sampleDescription?.required}
                                type="text"
                                isDisabled={viewTrfMode || trfData?.[designNo]?.sampleDescription}
                                value={trfFormData?.sampleDescription || ""}
                                onChange={(e) => handleInputChange("sampleDescription", e.target.value)}
                                inputClassName="bg-gray-50 border-gray-200  !whitespace-normal !text-clip !overflow-visible"
                                genInputExtraClass= "!whitespace-normal !overflow-visible !text-clip"
                            />
                        </div>
                        <div>
                            <GenericInput
                                label={formFields?.yarnCount?.label}
                                inputKey="yarnCount"
                                errorData={errorData}
                                hideInputTooltip={true}
                                setErrorData={setErrorData}
                                type="text"
                                mandatory={formFields?.yarnCount?.required}
                                value={trfFormData?.yarnCount || ""}
                                onChange={(e) => handleInputChange("yarnCount", e.target.value)}
                                isDisabled={viewTrfMode || trfData?.[designNo]?.yarnCount}
                                inputClassName="bg-gray-50 border-gray-200  !whitespace-normal !text-clip !overflow-visible"
                                genInputExtraClass= "!whitespace-normal !overflow-visible !text-clip"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-6 gap-4 mb-4">
                        <div>
                            <GenericInput
                                label={formFields?.fabricComposition?.label}
                                mandatory={formFields?.fabricComposition?.required}
                                inputKey="fabricComposition"
                                errorData={errorData}
                                hideInputTooltip={true}
                                setErrorData={setErrorData}
                                value={trfFormData?.fabricComposition}
                                isDisabled={viewTrfMode || trfData?.[designNo]?.fabricComposition}
                                onChange={(e) => handleInputChange("fabricComposition", e.target?.value)}
                                inputClassName="bg-gray-50 border-gray-200  !whitespace-normal !text-clip !overflow-visible"
                                genInputExtraClass= "!whitespace-normal !overflow-visible !text-clip"
                            />
                        </div>
                        <div>
                            <GenericInput
                                label={formFields?.color?.label}
                                mandatory={formFields?.color?.required}
                                inputKey="color"
                                errorData={errorData}
                                hideInputTooltip={true}
                                setErrorData={setErrorData}
                                value={trfFormData?.color}
                                isDisabled={viewTrfMode || trfData?.[designNo]?.color}
                                onChange={(e) => handleInputChange("color", e.target?.value)}
                                inputClassName="bg-gray-50 border-gray-200  !whitespace-normal !text-clip !overflow-visible"
                                genInputExtraClass= "!whitespace-normal !overflow-visible !text-clip"
                            />
                        </div>
                        <div>
                            <GenericInput
                                label={formFields?.fabricConstruction?.label}
                                mandatory={formFields?.fabricConstruction?.required}
                                inputKey="fabricConstruction"
                                errorData={errorData}
                                hideInputTooltip={true}
                                setErrorData={setErrorData}
                                type="text"
                                isDisabled={viewTrfMode || trfData?.[designNo]?.fabricConstruction}
                                value={trfFormData?.fabricConstruction || ""}
                                onChange={(e) => handleInputChange("fabricConstruction", e.target?.value)}
                                inputClassName="bg-gray-50 border-gray-200  !whitespace-normal !text-clip !overflow-visible"
                                genInputExtraClass= "!whitespace-normal !overflow-visible !text-clip"
                            />
                        </div>
                        <div>
                            <GenericInput
                                label={formFields?.endUse?.label}
                                mandatory={formFields?.endUse?.required}
                                inputKey="endUse"
                                errorData={errorData}
                                hideInputTooltip={true}
                                setErrorData={setErrorData}
                                type="text"
                                isDisabled={viewTrfMode || trfData?.[designNo]?.endUse}
                                value={trfFormData?.endUse || ""}
                                onChange={(e) => handleInputChange("endUse", e.target?.value)}
                                inputClassName="bg-gray-50 border-gray-200  !whitespace-normal !text-clip !overflow-visible"
                                genInputExtraClass= "!whitespace-normal !overflow-visible !text-clip"
                            />
                        </div>
                        <div>
                            <GenericInput
                                label={formFields?.fabricWeight?.label}
                                mandatory={formFields?.fabricWeight?.required}
                                inputKey="fabricWeight"
                                errorData={errorData}
                                hideInputTooltip={true}
                                setErrorData={setErrorData}
                                type="text"
                                isDisabled={viewTrfMode || trfData?.[designNo]?.fabricWeight}
                                value={trfFormData?.fabricWeight || ""}
                                onChange={(e) => handleInputChange("fabricWeight", e.target?.value)}
                                inputClassName="bg-gray-50 border-gray-200  !whitespace-normal !text-clip !overflow-visible"
                                genInputExtraClass= "!whitespace-normal !overflow-visible !text-clip"
                            />
                        </div>
                        <div>
                            <GenericInput
                                label={formFields?.typeOfPrint?.label}
                                mandatory={formFields?.typeOfPrint?.required}
                                inputKey="typeOfPrint"
                                errorData={errorData}
                                type="text"
                                hideInputTooltip={true}
                                isDisabled={viewTrfMode || trfData?.[designNo]?.typeOfPrint}
                                setErrorData={setErrorData}
                                value={trfFormData?.typeOfPrint}
                                onChange={(e) => handleInputChange("typeOfPrint", e.target?.value)}
                                inputClassName="bg-gray-50 border-gray-200  !whitespace-normal !text-clip !overflow-visible"
                                genInputExtraClass= "!whitespace-normal !overflow-visible !text-clip"
                            />
                        </div>
                    </div>
                </div>


                <div className="grid grid-cols-2 gap-6 mb-8 w-full">
                    <div className="col-span-1">
                        <div className="flex justify-between">
                            <span className="text-[#67768e] font-bold text-xs block mb-3">
                            {formFields?.careInstructions?.label} {formFields?.careInstructions?.required && <span className='mandatory'>*</span>}
                        </span>
                        <span>
                            <label className="generic-checkbox">
                                                <input
                                                    type="checkbox"
                                                    disabled={viewTrfMode}
                                                    checked={selectAllCareInstruction}
                                                    onChange={(e) => {
                                                        setSelectAllCareInstructions(prev=>{
                                                            const nextVal= !prev;
                                                            handleInputChange('careInstructions',testTrfData?.['TRF_CARE_INSTRUCTIONS'] || [],nextVal);
                                                            return nextVal;
                                                        })
                                                    }}
                                                />

                                                <span className={`checkmark1 ${viewTrfMode ? "opacity-25" : ""}`}></span>


                                                <span className="text-[#67768e] text-xs font-bold">
                                                    Select All
                                                </span>
                                            </label>
                        </span>
                        </div>
                        <div className="flex gap-3 col-span-1">
                            {testTrfData?.['TRF_CARE_INSTRUCTIONS']?.map((item) => {
                                const isSelected = trfFormData?.careInstructions?.includes(item)
                                const iconData = careInstructionIcons.find(c => c.key === item);
                                return (
                                    <div
                                        key={item}
                                        className={`relative w-full h-[90px] flex flex-col items-center justify-center 
            border rounded-lg transition-all duration-200
            border-gray-200 bg-white ${!viewTrfMode ? "hover:border-[var(--primary-color)] cursor-pointer" : "cursor-not-allowed"}`}
                                        onClick={() => handleInputChange("careInstructions", item)}
                                    >
                                        <div className="absolute top-2 left-2" onClick={(e) => e.stopPropagation()}>
                                            <label className="generic-checkbox">
                                                <input
                                                    type="checkbox"
                                                    disabled={viewTrfMode}
                                                    checked={isSelected || false}
                                                    onChange={() => handleInputChange("careInstructions", item)}
                                                />
                                                <span className={`checkmark1 ${viewTrfMode ? "opacity-25" : ""}`}></span>
                                            </label>
                                        </div>

                                        {iconData && <div className="mb-2 text-gray-700">{iconData.icon}</div>}
                                        <span className="text-xs font-medium text-gray-700">
                                            {item}
                                        </span>
                                    </div>
                                )
                            })}
                        </div>

                    </div>

                    <div className="col-span-1 max-h-[90px]">
                        <GenericInput
                            label={formFields?.otherCareInstruction?.label}
                            mandatory={formFields?.otherCareInstruction?.required}
                            inputKey="otherCareInstruction"
                            errorData={errorData}
                            setErrorData={setErrorData}
                            type="textarea"
                            value={trfFormData?.otherCareInstruction}
                            onChange={(e) => handleInputChange("otherCareInstruction", e.target?.value)}
                            rows={1}
                            inputClassName="bg-gray-50 border border-gray-200 min-h-full w-full"
                            genInputExtraClass="h-full w-full resize-none textarea-w-scroll"
                            gibInnerClassName="!h-full w-full"
                            isDisabled={viewTrfMode || trfData?.[designNo]?.otherCareInstruction}
                        />
                    </div>
                </div>

                <div className="mb-4">
                    <h3 className="text-sm font-bold text-gray-900 mb-4">
                        Tests Required
                    </h3>

                    <div className="grid grid-cols-1 lg:grid-cols-[0.7fr_1fr_1fr] gap-0">
                        <div className="border-r border-gray-200 pr-6">
                            <div className="mb-4">
                                <span className="text-xs font-bold text-gray-900 uppercase tracking-wide">{formFields?.packageTestFPT?.label}{formFields?.packageTestFPT?.required && <span className='mandatory'>*</span>}</span>
                            </div>
                            <div>
                                {testTrfData?.['TRF_FPT_TEST']?.length > 2 && <div className="mb-2">
                                    <CustomCheckbox
                                        checked={selectAllTest.packageTestFPT}
                                        onChange={() => {
                                            setSelectAllTest(prev => {
                                                const nextVal = !prev.packageTestFPT
                                                handleInputChange('packageTestFPT', (testTrfData?.['TRF_FPT_TEST'] || []), nextVal)
                                                return {
                                                    ...prev,
                                                    packageTestFPT: nextVal
                                                }
                                            })
                                        }}
                                        label={<b>Select All</b>}
                                    />
                                </div>}
                                <div className="mt-2">
                                    {(testTrfData?.['TRF_FPT_TEST'] || [])?.map(test => (
                                        <div key={test} className="mb-2">
                                            <CustomCheckbox
                                                checked={trfFormData?.packageTestFPT?.includes(test)}
                                                onChange={() => handleInputChange('packageTestFPT', test)}
                                                label={test}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="border-r border-gray-200 px-6">
                            <div className="mb-4">
                                <span className="text-xs font-bold text-gray-900 uppercase tracking-wide">{formFields?.packageTestGPT?.label}{formFields?.packageTestGPT?.required && <span className='mandatory'>*</span>}</span>
                            </div>
                            <div>
                                {testTrfData?.['TRF_GPT_TEST']?.length > 2 && <div className="mb-2">
                                    <CustomCheckbox
                                        checked={selectAllTest.packageTestGPT}
                                        onChange={() => {
                                            setSelectAllTest(prev => {
                                                const nextVal = !prev.packageTestGPT
                                                handleInputChange('packageTestGPT', (testTrfData?.['TRF_GPT_TEST'] || []), nextVal)
                                                return {
                                                    ...prev,
                                                    packageTestGPT: nextVal
                                                }
                                            })
                                        }}
                                        label={<b>Select All</b>}
                                    />
                                </div>}
                                <div className="mt-2">
                                    {testTrfData?.['TRF_GPT_TEST']?.map(test => (
                                        <div key={test} className="mb-2">
                                            <CustomCheckbox
                                                checked={trfFormData?.packageTestGPT?.includes(test)}
                                                onChange={() => handleInputChange('packageTestGPT', test)}
                                                label={test}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="pl-6">
                            <div className="mb-4">
                                <span className="text-xs font-bold text-gray-900 uppercase tracking-wide">{formFields?.additionalTestRequired?.label}{formFields?.additionalTestRequired?.required && <span className='mandatory'>*</span>}</span>
                            </div>
                            <div>
                                {testTrfData?.['TRF_ADDITIONAL_TEST']?.length > 2 && <div className="mb-2">
                                    <CustomCheckbox
                                        checked={selectAllTest.additionalTestRequired}
                                        onChange={() => {
                                            setSelectAllTest(prev => {
                                                const nextVal = !prev.additionalTestRequired
                                                handleInputChange('additionalTestRequired', (testTrfData?.['TRF_ADDITIONAL_TEST'] || []), nextVal)
                                                return {
                                                    ...prev,
                                                    additionalTestRequired: nextVal
                                                }
                                            })
                                        }}
                                        label={<b>Select All</b>}
                                    />
                                </div>}
                                <div className="mt-2 grid grid-cols-2 gap-x-4">
                                    {testTrfData?.['TRF_ADDITIONAL_TEST']?.map(test => (
                                        <div key={test} className="mb-2">
                                            <CustomCheckbox
                                                checked={trfFormData.additionalTestRequired?.includes(test)}
                                                onChange={() => handleInputChange('additionalTestRequired', test)}
                                                label={test}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                </>)}
            </div>

            {viewTrfMode || isDirectTRFPending ? null : (
                <div className="sticky bottom-0 z-20 px-6 py-4 border-t border-gray-200 bg-white flex justify-end gap-3">
                    <GenericCustomButton
                        label="Clear Data"
                        type="default"
                        onClick={handleClearData}
                        className="bg-gray-100 text-gray-700 border-none hover:bg-gray-200"
                    />
                    <GenericCustomButton
                        label="Submit TRF"
                        type="primary"
                        onClick={handleSubmit}
                        className="text-white"
                    />
                </div>)}

            {showConfirmationModal && (
                <ParentModal
                    getParentChildProps={{
                        closeModal: () => setShowConfirmationModal(false),
                        animationType: 'bottom',
                        parentModalOuterMostClassName: 'flex items-center justify-center',
                        postitionProps: { position: 'relative' },
                        dimensions: {
                            width: "36vw",
                            height: "fit-content",
                            maxWidth: '500px',
                            maxHeight: '50vh',
                            overflowY: 'auto'
                        },
                        icon: <Icons.DangerIcon height={"3rem"} width={"3rem"} />,
                        buttons: [
                            {
                                type: 'outline',
                                label: "Cancel",
                                onClick: () => setShowConfirmationModal(false)
                            },
                            {
                                type: 'primary',
                                label: "Yes, Proceed",
                                onClick: () => {
                                    setDesignNo(pendingDesignNo);
                                    setShowConfirmationModal(false);
                                }
                            }
                        ],
                        labelComponent: "Confirm Design Number update?",
                        message: () => (
                            <p className="text-gray-600 text-sm">
                                This action will update all TRF Details you may have filled so far.
                            </p>
                        )
                    }}
                    childComponent={GenericConfirmationModal}
                />
            )}
        </div>
    );
};


export default React.memo(TrfModal);